import { Button, Dropdown, BhiFormDialog, TextInput } from 'src/UIKit';
import { InputAdornment } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useBlocklistApi } from 'src/modules/blocklist/api/BlocklistApi';
import {
  useCreateContactCustomFieldApi,
  useUpdateContactCustomFieldApi,
  useUpdateCustomCompanyFieldApi,
  useUpdateDefaultCustomFieldsValues,
} from 'src/api/customFields';
import BannedWordsModal from 'src/components/BannedWordsModal/view';
import { ContactMetadataType, CustomFieldFormData } from 'src/constants/types';
import {
  customFieldValidationDescriptionSchema,
  customFieldValidationNameSchema,
  ValidationErrors,
} from 'src/constants/validations';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import * as Yup from 'yup';
import { firstLetterCamelCase } from 'src/constants/helpers';

type CreateCustomFieldModalType = {
  show: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  customFieldData?: CustomFieldFormData;
  refreshCustomFields: () => void;
  isCompanyFields: boolean;
};
const initialValue = {
  name: '',
  description: '',
  type: 'string',
  defaultValue: '',
};

export default function CreateCustomFieldModal({
  show,
  setShowModal,
  customFieldData,
  refreshCustomFields,
  isCompanyFields,
}: CreateCustomFieldModalType) {
  const isStandardField = (field: CustomFieldFormData | undefined) => {
    if (!field?.id) {
      return false;
    }
    return field.metadataId == null && field.id.includes('.standard.');
  };
  const editingStandardField = isStandardField(customFieldData);

  const [formValues, setFormValues] = useState<{
    name: string;
    description: string;
    type: string;
    defaultValue: string | null;
  }>(initialValue);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [showbannedWordsModal, setShowBannedWordsModal] = useState<boolean>(false);
  const createCustomField = useCreateContactCustomFieldApi();
  const updateContactCustomField = useUpdateContactCustomFieldApi();
  const updateCompanyField = useUpdateCustomCompanyFieldApi();
  const updateDefaultCustomFieldsValues = useUpdateDefaultCustomFieldsValues();
  const validateText = useBlocklistApi().validate;
  const selectedCompanyId = useSelector(getCurrentCompanyId);

  const handleClose = () => {
    setShowModal(false);
    setFormValues(initialValue);
    setErrors({});
  };

  const handleSelectChange = (type: string) => {
    setFormValues({ ...formValues, type, defaultValue: type === 'datetime' ? null : '' });
  };

  const handleSaveError = (e: { error?: string | { message?: string } }) => {
    const isConflict =
      e.error === 'conflict' ||
      (typeof e.error === 'object' && e.error?.message?.includes('already exists'));

    if (isConflict) {
      setErrors({ name: 'Entity with this name already exists' });
      return;
    }

    console.log(e);
  };

  const validateBlockListAndSave = (skipValidation: boolean = false) => {
    if (skipValidation) {
      handleCustomFieldSave();
    } else {
      const texts = editingStandardField
        ? [formValues.defaultValue || '']
        : [formValues.name, formValues.description, formValues.defaultValue || ''];
      validateText({
        texts,
      })
        .then(() => {
          handleCustomFieldSave();
        })
        .catch((error: { code: number }) => {
          if (error.code === 422) {
            setShowBannedWordsModal(true);
          }
          console.log(error);
        });
    }
  };

  const handleCustomFieldSave = () => {
    const isValid = editingStandardField ? true : validate(formValues);
    if (!isValid || !selectedCompanyId) {
      return;
    }

    const defaultValue = formValues.defaultValue?.trim();

    if (editingStandardField && customFieldData?.id) {
      updateDefaultCustomFieldsValues(
        [{ name: customFieldData.id, value: defaultValue || null }],
        selectedCompanyId,
      )
        .then(() => {
          handleClose();
          refreshCustomFields();
        })
        .catch(handleSaveError);
      return;
    }

    if (customFieldData?.metadataId) {
      if (!isCompanyFields) {
        updateContactCustomField(
          {
            name: formValues.name,
            description: formValues.description,
            defaultValue: defaultValue || null,
          },
          customFieldData.metadataId,
        )
          .then(() => {
            handleClose();
            refreshCustomFields();
          })
          .catch(handleSaveError);
      } else {
        updateCompanyField(
          {
            metadataId: customFieldData.metadataId,
            value: defaultValue || '',
          },
          selectedCompanyId,
        )
          .then(() => {
            handleClose();
            refreshCustomFields();
          })
          .catch(handleSaveError);
      }
      return;
    }

    createCustomField(
      {
        name: formValues.name,
        description: formValues.description,
        type: formValues.type as ContactMetadataType,
        defaultValue: defaultValue || null,
      },
      selectedCompanyId,
    )
      .then(() => {
        handleClose();
        refreshCustomFields();
      })
      .catch(handleSaveError);
  };

  useEffect(() => {
    if (customFieldData) {
      setFormValues(customFieldData);
    } else {
      setFormValues(initialValue);
    }

    return () => {
      setFormValues({
        name: '',
        description: '',
        type: 'string',
        defaultValue: '',
      });
    };
  }, [customFieldData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newValues = { ...formValues, [name]: value };
    if (value.length > 1) {
      validate(newValues, false);
    }
    setFormValues(newValues);
  };

  const isValidForm = (values: typeof formValues) => {
    try {
      customFieldValidationNameSchema.validateSync(values, {
        abortEarly: false,
      });
      if (!isCompanyFields) {
        customFieldValidationDescriptionSchema.validateSync(values, {
          abortEarly: false,
        });
      }

      return true;
    } catch {
      return false;
    }
  };

  const validate = (values: typeof formValues, checkDescription = true) => {
    try {
      if (checkDescription && !isCompanyFields) {
        customFieldValidationDescriptionSchema.validateSync(values, {
          abortEarly: false,
        });
      }
      customFieldValidationNameSchema.validateSync(values, {
        abortEarly: false,
      });

      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  const getDefaultValueInputType = (type: string) => {
    switch (type) {
      case 'number':
      case 'dollars':
        return 'number';
      case 'string':
        return 'string';
      case 'date':
        return 'date';
      default:
        'string';
    }
  };

  return (
    <Fragment>
      <BhiFormDialog
        open={show}
        onClose={handleClose}
        title={`${customFieldData ? 'Update ' : 'Create '}${isCompanyFields ? customFieldData?.description || '' : 'Contact Field'}`}
        actions={
          <>
            <Button variant="outlined" onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              disabled={editingStandardField ? false : !isValidForm(formValues)}
              onClick={() => validateBlockListAndSave()}
            >
              Save
            </Button>
          </>
        }
      >
        {!isCompanyFields && (
          <TextInput
            label="Display Name"
            name="description"
            margin="none"
            disabled={editingStandardField}
            value={formValues.description}
            onChange={handleChange}
            error={!!errors.description}
            helperText={errors.description}
          />
        )}
        {!isCompanyFields && (
          <TextInput
            label="Merge Tag"
            name="name"
            margin="none"
            disabled={editingStandardField}
            value={formValues.name}
            onChange={handleChangeName}
            error={!!errors.name}
            helperText={errors.name}
          />
        )}
        {!customFieldData?.id && !isCompanyFields && (
          <Dropdown
            label="Type"
            margin="none"
            value={formValues.type}
            onChange={(_, type) => handleSelectChange(type)}
            options={[
              { value: 'string', title: 'Text' },
              { value: 'number', title: 'Number' },
              { value: 'date', title: 'Date Only' },
              { value: 'datetime', title: 'Date and Time' },
              { value: 'dollars', title: 'Dollar Value' },
            ]}
          />
        )}
        {customFieldData?.id && !isCompanyFields && (
          <TextInput
            label="Type"
            margin="none"
            disabled
            value={firstLetterCamelCase(formValues.type)}
            onChange={() => undefined}
          />
        )}
        {formValues.type === 'date' && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              slotProps={{ textField: { error: false, size: 'small', fullWidth: true } }}
              name="defaultValue"
              format="MM/DD/YYYY"
              closeOnSelect
              onChange={(value: Dayjs | null | undefined) =>
                setFormValues({
                  ...formValues,
                  defaultValue: value ? value.format('YYYY-MM-DD') : '',
                })
              }
              label={isCompanyFields ? customFieldData?.description : 'Default Value'}
              value={formValues.defaultValue === 'null' ? null : dayjs(formValues.defaultValue)}
            />
          </LocalizationProvider>
        )}
        {formValues.type === 'datetime' && (
          <TextInput
            label="Default Value"
            margin="none"
            disabled
            value="No default value can be set for Date and Time fields"
            helperText="Date and Time fields require both date and time to be entered by the user"
            onChange={() => undefined}
          />
        )}
        {formValues.type !== 'date' && formValues.type !== 'datetime' && (
          <TextInput
            label={isCompanyFields ? customFieldData?.description : 'Default Value'}
            name="defaultValue"
            margin="none"
            type={getDefaultValueInputType(formValues.type)}
            InputProps={{
              startAdornment: formValues.type === 'dollars'
                ? <InputAdornment position="start">$</InputAdornment>
                : undefined,
            }}
            value={formValues.defaultValue || ''}
            onChange={handleChange}
          />
        )}
      </BhiFormDialog>
      <BannedWordsModal
        isOpen={showbannedWordsModal}
        onConfirm={() => {
          validateBlockListAndSave(true);
          setShowBannedWordsModal(false);
        }}
        onCancel={() => setShowBannedWordsModal(false)}
      />
    </Fragment>
  );
}
